import { getApiErrorMessage } from '../../utils/apiError';
import { authService } from '../../services/auth';
import { getBlockedActionMessage, normalizeRelatedDocuments } from '../warehouse/blockedActions';

/**
 * Single home for turning a customer-order API failure into a Polish sentence.
 * Sibling of `modules/warehouse/pzErrorMessages.ts`; the same keys render the
 * `blockedActions` alert on the order card (see `blockedActions.ts`).
 *
 * Exception: ORDER_NUMBER_TAKEN (manual number already used by another order)
 * lives in `orderProductSaveErrors.ts` (`getOrderNumberTaken`), next to the
 * create / draft-save mapper — importing this file pulls in axios via `auth`,
 * which that dependency-free mapper and its Jest test must avoid.
 */
export const ORDER_ERROR_CODE_KEYS: Record<string, string> = {
  ORDER_STATUS_SET_BY_INVOICE: 'orders:error.statusSetByInvoice',
  ORDER_INVALID_STATUS_TRANSITION: 'orders:error.invalidStatusTransition',
  ORDER_DELIVERY_WZ_MISSING: 'orders:error.deliveryWzMissing',
  // WP1 part 2: the invoice no longer blocks editing; only references stay.
  // WP12 B1: transports, unreleased WZ and loaded cargo are confirmed warnings
  // of the delete / line removal (blockedActions `warning: true`); an invoice
  // or a released WZ refuses the delete with "Anuluj i ukryj" on offer.
  // ORDER_PRODUCT_INVOICED_LINE_REMOVAL_BLOCKED: older servers only.
  ORDER_INVOICED_DELETE_BLOCKED: 'orders:error.invoicedDeleteBlocked',
  ORDER_PRODUCT_INVOICED_LINE_REMOVAL_BLOCKED: 'orders:error.invoicedLineRemovalBlocked',
  ORDER_PRODUCT_INVOICED_UNIT_CHANGE_BLOCKED: 'orders:error.invoicedUnitChangeBlocked',
  ORDER_LINKED_TO_TRANSPORTS: 'orders:error.linkedToTransports',
  ORDER_LINKED_TO_WZ_DOCUMENTS: 'orders:error.linkedToWzDocuments',
  ORDER_PRODUCT_HAS_TRANSPORT_CARGO: 'orders:error.productHasTransportCargo',
};

const ORDER_BLOCKING_ERROR_CODES = new Set([
  ...Object.keys(ORDER_ERROR_CODE_KEYS),
  'INVOICE_APPROVAL_REQUIRED',
]);

export function isBlockingOrderError(error: any): boolean {
  const code = error?.response?.data?.code;
  return typeof code === 'string' && ORDER_BLOCKING_ERROR_CODES.has(code);
}

/**
 * `INVOICE_APPROVAL_REQUIRED` points the operator to the sales-invoice form —
 * but only users on the invoice allowlist can open it. Everyone else is told
 * to hand the document over instead of being sent to a screen they cannot see.
 */
function invoiceApprovalRequiredKey(): string {
  return authService.canManageInvoiceImports()
    ? 'orders:error.invoiceApprovalRequired'
    : 'orders:error.invoiceApprovalRequiredNoAccess';
}

/**
 * Older delete rejections carry `transportRouteNumbers` / `wzNumbers` rather
 * than `relatedDocuments`; keep naming the documents either way.
 */
function relatedDocumentsFromPayload(data: any) {
  if (Array.isArray(data?.relatedDocuments) && data.relatedDocuments.length > 0) {
    return normalizeRelatedDocuments(data.relatedDocuments);
  }
  const numbers: string[] = Array.isArray(data?.transportRouteNumbers) ? data.transportRouteNumbers
    : Array.isArray(data?.wzNumbers) ? data.wzNumbers
    : Array.isArray(data?.routeNumbers) ? data.routeNumbers : [];
  const type = Array.isArray(data?.wzNumbers) ? 'WZ' : 'TRANSPORT';
  return normalizeRelatedDocuments(numbers.map((number) => ({ type, number })));
}

export function getOrderErrorMessage(
  error: any,
  t: any,
  fallbackKey: string,
  fallbackParams?: Record<string, unknown>,
): string {
  const data = error?.response?.data;
  const code = data?.code;
  if (code === 'INVOICE_APPROVAL_REQUIRED') {
    return t(invoiceApprovalRequiredKey());
  }
  if (typeof code === 'string' && ORDER_ERROR_CODE_KEYS[code]) {
    const relatedDocuments = relatedDocumentsFromPayload(data);
    if (relatedDocuments.length > 0) {
      return getBlockedActionMessage({ code, relatedDocuments }, t, 'orders', ORDER_ERROR_CODE_KEYS);
    }
    return t(ORDER_ERROR_CODE_KEYS[code]);
  }
  return getApiErrorMessage(error, t, fallbackKey, undefined, fallbackParams);
}

/**
 * A manual status change refused because the card showed an outdated status: the backend
 * refuses a change to the status the order already has (400 ORDER_INVALID_STATUS_TRANSITION)
 * or a racing one (409 without a code). The card reloads the order after such an error.
 */
export function isOrderStatusConflictError(error: any): boolean {
  const failedStatus = error?.response?.status;
  return error?.response?.data?.code === 'ORDER_INVALID_STATUS_TRANSITION'
    || (failedStatus === 409 && !error?.response?.data?.code);
}

/**
 * A refused manual status change stays on screen until clicked only when it blocks
 * something the operator must handle (e.g. the status set by the invoice). „Zamówienie ma już
 * ten status” blocks nothing — the card reloads and shows that status — so it closes itself.
 */
export function isStickyOrderStatusRefusal(error: any): boolean {
  return isBlockingOrderError(error) && !isOrderStatusConflictError(error);
}

/**
 * After the reload: when the saved status differs from the one the card showed, someone else
 * changed it — say so and name the status the card now shows (the same label as the success
 * toast). `null` when the status did not move (then the generic refusal text applies).
 */
export function getOrderStatusConflictMessage(
  t: (key: string, params?: Record<string, unknown>) => string,
  shownStatus: string | null | undefined,
  savedStatus: string | null | undefined,
): string | null {
  if (!savedStatus || savedStatus === shownStatus) return null;
  return t('orders:orderDetail.messages.statusConflict', { status: t(`orders:statusOptions.${savedStatus}`) });
}
